import { CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { memo, useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { EmptyData } from '~/components/EmptyData';
import { Pagination } from '~/components/Pagination';
import { ProgressBar } from '~/components/ProgressBar';
import { ROUTES } from '~/constants/routes';
import { GetPendingCompaniesResponse } from '~/services/rtkQuery/company/types';
import { capitalize } from '~/utils/helpers';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

interface Props {
  fetchLimit: number;
  loading: boolean;
  currentPage: number;
  data: GetPendingCompaniesResponse['result'] | null;
  onPageChange: (page: number) => void;
}

const Component: React.FC<Props> = ({ fetchLimit, loading, currentPage, data, onPageChange }) => {
  const navigate = useNavigate();

  const handleCompanyClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, id: number) => {
      // @ts-expect-error tagName exists
      if (event.target.tagName !== 'A') {
        navigate(generatePath(ROUTES.EDIT_COMPANY, { id: id.toString() }));
      }
    },
    [navigate],
  );

  const pendingTotalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: data?.total || 0,
      fetchLimit,
    });
  }, [data?.total, fetchLimit]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (data?.data.length === 0) {
    return <EmptyData description="No companies available" title="Pending" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles['container__table-container']}>
        <table className={styles.container__table}>
          <thead>
            <tr>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Company Name
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Responsible
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Updated
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Expires
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Progress Bar
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Profile Status
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.data.map(company => (
              <tr key={company.id} onClick={event => handleCompanyClick(event, company.id)}>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {company.name}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {company.user ? `${company.user.fist_name} ${company.user.last_name}` : 'N/A'}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {format(new Date(company.updated_at), 'MMM dd, yyyy')}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {format(new Date(company.expired_at), 'MMM dd, yyyy')}
                  </Typography>
                </td>
                <td aria-label="Progress Bar">
                  <ProgressBar
                    className={styles['container__progress-bar']}
                    label="Profile Completed"
                    value={+(company.progress_bar * 100).toFixed(0)}
                  />
                </td>
                <td>
                  <Typography
                    color="var(--yellow-1-color)"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {capitalize(company.status)}
                  </Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.container__footer}>
        <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
          {getCurrentPageInfo({
            currentPage,
            fetchLimit,
            currentPageItemsCount: data?.data.length || 0,
            totalItemsCount: data?.total || 0,
          })}
        </Typography>
        <Pagination
          currentPage={currentPage}
          totalPages={pendingTotalPages}
          onChange={onPageChange}
        />
      </div>
    </div>
  );
};

export const PendingDeals = memo(Component);
